import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getUser, updateUser, disableUser, deleteUser, getUserActivity, type User, type UpdateUserData, type UserActivity } from '../../api/users';
import { getUserUsage, type UserUsage } from '../../api/usage';
import { StatusBadge } from '../common/StatusBadge';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Modal } from '../common/Modal';
import { UserForm } from './UserForm';
import { SpendChart } from '../usage/SpendChart';

export function UserDetailView() {
  const { username } = useParams<{ username: string }>();
  const { getJwtToken } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [usage, setUsage] = useState<UserUsage | null>(null);
  const [activity, setActivity] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const fetchData = useCallback(async () => {
    if (!username) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const [userData, usageData, activityData] = await Promise.all([
        getUser(username, token),
        getUserUsage(username, token).catch(() => null),
        getUserActivity(username, token).catch(() => [] as UserActivity[]),
      ]);
      setUser(userData);
      setUsage(usageData);
      setActivity(activityData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [username, getJwtToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdate = async (data: UpdateUserData | Record<string, unknown>) => {
    if (!username) return;
    const token = await getJwtToken();
    if (!token) return;
    await updateUser(username, data as UpdateUserData, token);
    setShowEditModal(false);
    await fetchData();
  };

  const handleDisable = async () => {
    if (!username) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await disableUser(username, token);
      setShowDisableConfirm(false);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable user');
      setShowDisableConfirm(false);
    }
  };

  const handleEnable = async () => {
    if (!username) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await updateUser(username, { status: 'active' }, token);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable user');
    }
  };

  const handleDelete = async () => {
    if (!username) return;
    try {
      const token = await getJwtToken();
      if (!token) return;
      await deleteUser(username, token);
      setShowDeleteConfirm(false);
      navigate('/admin/users');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      setShowDeleteConfirm(false);
    }
  };

  if (loading && !user) {
    return <div className="view__empty">Loading user details...</div>;
  }

  if (!user) {
    return (
      <div className="view__empty">
        <p>User not found.</p>
        <button className="btn btn--secondary" onClick={() => navigate('/admin/users')} type="button">
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">{user.displayName || user.username}</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowEditModal(true)}
            type="button"
          >
            Edit
          </button>
          {user.status === 'active' ? (
            <button
              className="btn btn--danger"
              onClick={() => setShowDisableConfirm(true)}
              type="button"
            >
              Disable
            </button>
          ) : (
            <button
              className="btn btn--success"
              onClick={handleEnable}
              type="button"
            >
              Enable
            </button>
          )}
          <button
            className="btn btn--danger"
            onClick={() => setShowDeleteConfirm(true)}
            type="button"
          >
            Delete
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => navigate('/admin/users')}
            type="button"
          >
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {/* Budget Progress Bar */}
      {user.maxBudget != null && (
        (() => {
          const spent = usage?.totalSpend ?? user.spend ?? 0;
          const pct = Math.min((spent / user.maxBudget!) * 100, 100);
          const tier = pct >= 80 ? 'red' : pct >= 50 ? 'yellow' : 'green';
          return (
            <div className="budget-summary">
              <span className={`budget-summary__text budget-indicator__label--${tier}`}>
                {pct.toFixed(1)}% of ${user.maxBudget!.toFixed(2)} budget used
                (${spent.toFixed(2)} spent)
              </span>
              <div className="budget-summary__bar">
                <div className="budget-bar">
                  <div
                    className={`budget-bar__fill budget-bar__fill--${tier}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })()
      )}

      <section className="view__section">
        <h2 className="view__section-title">User Information</h2>
        <div className="settings-card">
          <div className="settings-card__row">
            <span className="settings-card__label">Username</span>
            <span className="settings-card__value">{user.username}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Email</span>
            <span className="settings-card__value">{user.email}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Display Name</span>
            <span className="settings-card__value">{user.displayName || '--'}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Role</span>
            <span className="settings-card__value">{user.role}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Status</span>
            <StatusBadge status={user.status === 'active' ? 'Active' : 'Disabled'} />
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Max Budget</span>
            <span className="settings-card__value">
              {user.maxBudget != null ? `$${user.maxBudget.toFixed(2)}` : 'Unlimited'}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">TPM Limit</span>
            <span className="settings-card__value">
              {user.tpmLimit != null ? user.tpmLimit.toLocaleString() : 'Unlimited'}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">RPM Limit</span>
            <span className="settings-card__value">
              {user.rpmLimit != null ? user.rpmLimit.toLocaleString() : 'Unlimited'}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Allowed CIDRs</span>
            <span className="settings-card__value">
              {user.allowedCidrs && user.allowedCidrs.length > 0
                ? user.allowedCidrs.join(', ')
                : 'Any'}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Created</span>
            <span className="settings-card__value">{new Date(user.createdAt).toLocaleString()}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Updated</span>
            <span className="settings-card__value">{new Date(user.updatedAt).toLocaleString()}</span>
          </div>
        </div>
      </section>

      {/* Spend chart */}
      <section className="view__section">
        <h2 className="view__section-title">Spend (Last 30 Days)</h2>
        {usage && usage.dailySpend && usage.dailySpend.length > 0 ? (
          <div className="chart-container">
            <SpendChart
              data={usage.dailySpend}
              title="Daily Spend"
              color="var(--accent-cyan)"
            />
          </div>
        ) : (
          <div className="settings-card settings-card--empty">No usage data available</div>
        )}
        {usage && (
          <div className="dashboard-kpis" style={{ marginTop: 12 }}>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                ${(usage.totalSpend ?? 0).toFixed(2)}
              </span>
              <span className="dashboard-kpi__label">Total Spend</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {(usage.totalTokens ?? 0).toLocaleString()}
              </span>
              <span className="dashboard-kpi__label">Total Tokens</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">
                {(usage.totalRequests ?? 0).toLocaleString()}
              </span>
              <span className="dashboard-kpi__label">Total Requests</span>
            </div>
          </div>
        )}
      </section>

      {/* Activity Timeline */}
      <section className="view__section">
        <h2 className="view__section-title">Activity</h2>
        {activity.length === 0 ? (
          <div className="settings-card settings-card--empty">No recent activity</div>
        ) : (
          <div className="timeline">
            {activity.map((item, idx) => {
              const icon =
                item.type === 'login' ? '\u{1F511}' :
                item.type === 'request' ? '\u{1F4E1}' :
                '\u{2699}\u{FE0F}';
              return (
                <div key={`${item.timestamp}-${idx}`} className="timeline__item">
                  <div className="timeline__dot" />
                  <div className="timeline__time">
                    {new Date(item.timestamp).toLocaleString()}
                  </div>
                  <div className="timeline__text">
                    <span style={{ marginRight: 6 }}>{icon}</span>
                    {item.details || item.action}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit User"
        wide
      >
        <UserForm
          user={user}
          isEdit
          onSubmit={handleUpdate}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      <ConfirmDialog
        isOpen={showDisableConfirm}
        title="Disable User"
        message={`Are you sure you want to disable user "${user.username}"? They will lose API access.`}
        confirmLabel="Disable"
        variant="danger"
        onConfirm={handleDisable}
        onCancel={() => setShowDisableConfirm(false)}
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Permanently Delete User"
        message={`Are you sure you want to permanently delete user "${user.username}"? This action cannot be undone. All user data will be removed from the system.`}
        confirmLabel="Delete Permanently"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
